import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import SearchDropdown from "../../input/SearchDropdown";
import { getOtherRefSearchDropdown } from "../../../api/keyvalue";
import { formatFromJsonDate, isAfter } from "../../../dateConfig";
import { getBehalfOf } from "../../../api/systemUserApi";
import DateTimePickerInput from "../../input/DateTimePickerInput";
import { useDispatch, useSelector } from "react-redux";
import { requestActions } from "../../../store/request-slice";
import LoadingSpinner from "../../LoadingSpinner";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import {
  dynamicRequestInitiationValidation,
  validateRequestInitiation,
} from "../../validations/validateRequestCreation";
import { FORMS, MODULES } from "../../../const";
import { shipmentsByCustomer } from "../../../api/customerShipmentApi";
import useFormVisibility from "../../../hooks/useFormVisibility";
import useDynamicValidation from "../../../hooks/useDynamicValidation";
import { isSameLocationsAllowed } from "../../../api/requestCreation";

const FORM = FORMS.REQUEST.CREATION_INFO.form;
const MAIN_FORM = FORMS.REQUEST.CREATION.form;

const RequestCreationInitiation = forwardRef(
  ({ dropdown, editMode, isLoading }, ref) => {
    const auth = useSelector(
      (state) => state.user.permissions[MODULES.Transaction]
    );

    const dispatch = useDispatch();

    const customerId = useSelector((state) => state.request.reqLCustomerId);

    const sameLocationAllowedCheckLoading = useSelector(
      (state) => state.request.sameLocationAllowedCheckLoading
    );

    const isFormVisibilityLoading = useSelector(
      (state) => state.formVisibility.loading
    );

    useFormVisibility({
      form: FORM,
      keyValue: customerId ? "stakeholder" : "default",
      type: customerId ?? 1,
      useDynamicFields: true,
    });

    const dynamicValidationStatus = useDynamicValidation(
      dynamicRequestInitiationValidation,
      FORM,
      true
    );

    const userData = useSelector((state) => state.user.userDetails);
    const user = useSelector((state) => state.user);
    const mainFormPermissions = auth?.[MAIN_FORM];
    const allowBackDates = mainFormPermissions?.FORM?.REQUEST_BACK_DATE;

    const plantRef = useRef();
    const viewOnlyMode = useSelector((state) => state.request.viewOnlyMode);
    const userDetails = useSelector((state) => state.user.userDetails);

    const showBehalfOf = userDetails.type === 1;

    const currentValues = useSelector(
      (state) => state.request.currentValues.requestCreationInit
    );
    const nextButtonErrors = useSelector(
      (state) => state.error.requestCreationErrors.requestErrors
    );

    const startArrivalTime = useSelector(
      (state) => state.request.startArrivalTime
    );
    console.log("Start arrival Time", formatFromJsonDate(startArrivalTime));
    const errors = useSelector((state) => state.request.requestInitErrors);

    const [requestInitiation, setRequestInitiation] = useState({
      reqLCustomerId: null,
      reqCustomerName: "",
      reqBehalfOf: "",
      reqBehalfOfUser: null,
      reqLPlantId: null,
      reqLPlantName: "",
      reqDepartment: null,
      reqBookingRequiredDate: "",
      reqBookingRequiredDateFormatted: "",
      reqContactPerson: "",
      reqContactNumber: "",
      reqContactEmail: "",
      reqShipmentType: 2,
      reqExportImport: 3,
      reqDeliveryCutoff: "",
      reqDeliveryCutoffFormatted: "",
      reqReferanceValue: "",
      reqInvoiceRef: "",
      reqVessel: "",
      reqVayage: "",
      reqConsolidateAllowed: 2,
      reqType: 1,
      reqPackages: null,
      reqPono: "",
      reqBlno: "",
      reqOperationType: "",
    });

    useEffect(() => {
      if (!startArrivalTime) return;

      setRequestInitiation((prev) => {
        const nextDate = startArrivalTime;
        const nextFmt = formatFromJsonDate(startArrivalTime);

        if (
          prev?.reqBookingRequiredDate === nextDate &&
          prev?.reqBookingRequiredDateFormatted === nextFmt
        ) {
          return prev;
        }
        return {
          ...prev,
          reqBookingRequiredDate: nextDate,
          reqBookingRequiredDateFormatted: nextFmt,
        };
      });
    }, [startArrivalTime]);

    useEffect(() => {
      (async () => {
        if (requestInitiation?.reqOperationType) {
          dispatch(requestActions.setSameLocationCheckLoading(true));
          const allowSameLoc = await isSameLocationsAllowed(
            requestInitiation?.reqOperationType,
            customerId
          );
          dispatch(requestActions.setAllowSameLocations(allowSameLoc));
          dispatch(requestActions.setSameLocationCheckLoading(false));
        } else {
          dispatch(requestActions.setAllowSameLocations(false));
        }
      })();
    }, [customerId, dispatch, requestInitiation?.reqOperationType]);

    const [currentErrors, setCurrentErrors] = useState({});
    const [operationTypeOptions, setOperationTypeOptions] = useState([]);

    useEffect(() => {
      (async () => {
        if (customerId) {
          const data = await shipmentsByCustomer(customerId);
          setOperationTypeOptions(data);
        } else {
          setOperationTypeOptions([]);
        }
      })();
    }, [customerId]);

    useEffect(() => {
      (async () => {
        if (!editMode) {
          if (userDetails) {
            setRequestInitiation((prevState) => ({
              ...prevState,
              reqContactPerson: `${userDetails.firstName} ${userDetails.lastName}`,
              reqContactNumber: userDetails?.contactNo ?? "",
              reqContactEmail: userDetails?.email ?? "",
            }));
          }
        }
      })();
    }, [editMode, userDetails, requestInitiation?.reqLCustomerId]);

    useEffect(() => {
      setCurrentErrors((prevState) => ({ ...prevState, ...nextButtonErrors }));
    }, [nextButtonErrors]);

    useEffect(() => {
      setCurrentErrors(errors);
    }, [errors]);

    useEffect(() => {
      if (editMode) {
        dispatch(
          requestActions.setRequest({
            reqLPlantId: currentValues?.reqLPlantId,
          })
        );
        setRequestInitiation(currentValues);
      }
    }, [editMode, currentValues, dispatch]);

    function validateEmail(email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(email);
    }

    const [searchFieldReset, setSearchFieldReset] = useState(false);

    useImperativeHandle(ref, () => ({
      getData: () => requestInitiation,
      validateForm: () => {
        return validateRequestInitiation(
          requestInitiation,
          dynamicValidationStatus
        );
      },
    }));

    console.log("requestInitiation", requestInitiation);

    return (
      <>
        {isLoading ||
        isFormVisibilityLoading ||
        sameLocationAllowedCheckLoading ? (
          <LoadingSpinner />
        ) : (
          <div>
            <form className="ui form">
              <div className="ui segment">
                <h4 className="ui dividing header">Request Initiation</h4>
                <div className="field">
                  <div className={showBehalfOf ? "two fields" : "field"}>
                    {userData.type === 1 ? (
                      <SearchDropdown
                        key={1}
                        readOnly={editMode}
                        editMode={editMode}
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        value={requestInitiation?.reqLCustomerId}
                        clearable={true}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="req_customer_id"
                        multiGrid={true}
                        multiGridCurrentValue={
                          requestInitiation?.reqCustomerName
                        }
                        onClear={() => {
                          dispatch(
                            requestActions.setRequest({ reqLCustomerId: null })
                          );
                          setRequestInitiation((prevState) => ({
                            ...prevState,
                            reqLPlantId: null,
                            reqLPlantName: "",
                            reqLCustomerId: null,
                            reqContactPerson: "",
                            reqContactNumber: "",
                            reqCustomerName: "",
                            reqContactEmail: "",
                          }));
                          plantRef?.current?.reset();
                        }}
                        onResultSelect={(data) => {
                          dispatch(
                            requestActions.setRequest({
                              reqLCustomerId: data.value,
                            })
                          );
                          setRequestInitiation((prevState) => ({
                            ...prevState,
                            reqLCustomerId: data.value,
                            reqCustomerName: data.title,
                            reqLPlantId: null,
                            reqLPlantName: "",
                          }));
                          plantRef?.current?.reset();
                        }}
                        returnFullObject={true}
                        api={getOtherRefSearchDropdown}
                        apiDependency={{
                          type: "stakeholder_type",
                          value: "Customer",
                        }}
                        required={true}
                        label="Customer"
                        error={currentErrors.reqLCustomerId}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { reqLCustomerId: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                      />
                    ) : (
                      <SearchDropdown
                        key={1}
                        readOnly={editMode}
                        editMode={editMode}
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        value={requestInitiation?.reqLCustomerId}
                        clearable={true}
                        form={FORM}
                        module={MODULES.Transaction}
                        returnFullObject={true}
                        field="req_customer_id"
                        onClear={() => {
                          dispatch(
                            requestActions.setRequest({ reqLCustomerId: null })
                          );
                          setRequestInitiation((prevState) => ({
                            ...prevState,
                            reqLPlantId: null,
                            reqLPlantName: "",
                            reqLCustomerId: null,
                            reqContactPerson: "",
                            reqContactNumber: "",
                            reqCustomerName: "",
                            reqContactEmail: "",
                          }));
                          plantRef?.current?.reset();
                        }}
                        onResultSelect={(data) => {
                          dispatch(
                            requestActions.setRequest({
                              reqLCustomerId: data.value,
                            })
                          );
                          setRequestInitiation((prevState) => ({
                            ...prevState,
                            reqLCustomerId: data.value,
                            reqCustomerName: data.title,
                            reqLPlantId: null,
                            reqLPlantName: "",
                          }));
                          plantRef?.current?.reset();
                        }}
                        api={getOtherRefSearchDropdown}
                        multiGrid={true}
                        multiGridCurrentValue={
                          requestInitiation?.reqCustomerName
                        }
                        checkCount={true}
                        apiDependency={{
                          type: "user_stakeholder",
                          value: "Requesting Customer",
                          param2: user.username,
                        }}
                        required={true}
                        label="Customer"
                        error={currentErrors.reqLCustomerId}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { reqLCustomerId: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                      />
                    )}

                    {showBehalfOf && (
                      <SearchDropdown
                        key={1}
                        useDynamicFields={true}
                        editMode={editMode}
                        readOnly={editMode}
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        value={requestInitiation?.reqBehalfOf}
                        clearable={true}
                        form={FORM}
                        module={MODULES.Transaction}
                        field="req_behalf_of"
                        returnFullObject={true}
                        multiGrid={true}
                        multiGridCurrentValue={
                          requestInitiation?.reqBehalfOfUser ?? ""
                        }
                        onClear={() => {
                          setRequestInitiation((prevState) => ({
                            ...prevState,
                            reqLPlantId: null,
                            reqLPlantName: "",
                            reqBehalfOf: -1,
                            reqBehalfOfUser: null,
                            reqContactPerson: `${userDetails?.firstName} ${userDetails?.lastName}`,
                            reqContactNumber: userDetails?.contactNo ?? "",
                            reqContactEmail: userDetails?.email ?? "",
                          }));

                          dispatch(
                            requestActions.setRequest({
                              reqLPlantId: null,
                            })
                          );
                        }}
                        onResultSelect={(data) => {
                          dispatch(
                            requestActions.setRequest({
                              reqLPlantId: null,
                            })
                          );

                          setRequestInitiation((prevState) => ({
                            ...prevState,
                            reqLPlantId: null,
                            reqLPlantName: "",
                            reqBehalfOf: data.value,
                            reqBehalfOfUser: data.usrUsername,
                            reqContactPerson: `${data.usrFirstname} ${data.usrSecondName}`,
                            reqContactNumber: data.usrContact ?? "",
                            reqContactEmail: data.usrEmail ?? "",
                          }));
                        }}
                        api={getBehalfOf}
                        apiDependency={{
                          customer:
                            requestInitiation?.reqLCustomerId?.toString(),
                          shipper: requestInitiation?.reqLPlantId?.toString(),
                        }}
                        fieldInfo={{
                          tableName: "authentication.user",
                          primaryKey: "user_seq",
                          fieldName: "username",
                          id: requestInitiation?.reqBehalfOf,
                        }}
                        error={currentErrors.reqBehalfOf}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { reqBehalfOf: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        label="Behalf of"
                      />
                    )}
                  </div>
                  <h4 className="ui dividing header">Plant/Shipper Info</h4>
                  <div className="field">
                    <SearchDropdown
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      disabled={
                        requestInitiation?.reqLCustomerId === null ||
                        requestInitiation?.reqLCustomerId === ""
                      }
                      ref={plantRef}
                      key={1}
                      editMode={editMode}
                      reset={searchFieldReset}
                      setReset={setSearchFieldReset}
                      value={requestInitiation?.reqLPlantId}
                      clearable={true}
                      returnFullObject={true}
                      form={FORM}
                      module={MODULES.Transaction}
                      multiGrid={true}
                      multiGridCurrentValue={
                        requestInitiation?.reqLPlantName ?? ""
                      }
                      field="req_plant_id"
                      onClear={() => {
                        dispatch(
                          requestActions.setRequest({
                            reqLPlantId: null,
                          })
                        );
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqLPlantId: null,
                          reqLPlantName: "",
                        }));
                      }}
                      onResultSelect={(data) => {
                        dispatch(
                          requestActions.setRequest({
                            reqLPlantId: data.value,
                          })
                        );
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqLPlantId: data.value,
                          reqLPlantName: data.title,
                        }));
                      }}
                      api={getOtherRefSearchDropdown}
                      apiDependency={
                        userData.type === 1
                          ? requestInitiation?.reqBehalfOfUser &&
                            requestInitiation?.reqBehalfOfUser !== -1
                            ? {
                                type: "user_stakeholder",
                                value: "Shipper",
                                param1:
                                  requestInitiation?.reqLCustomerId?.toString(),
                                param2: requestInitiation?.reqBehalfOfUser,
                              }
                            : {
                                type: "stakeholder_type",
                                value: "Shipper",
                                param2:
                                  requestInitiation?.reqLCustomerId?.toString(),
                              }
                          : {
                              type: "user_stakeholder",
                              value: "Shipper",
                              param1:
                                requestInitiation?.reqLCustomerId?.toString(),
                              param2: user.username,
                            }
                      }
                      fieldInfo={{
                        tableName: "masterdata.tms_m_stakeholder",
                        primaryKey: "stk_id",
                        fieldName: "stk_name",
                        id: requestInitiation?.reqLPlantId,
                      }}
                      required={true}
                      label="Plant Name"
                      checkCount={!!requestInitiation?.reqLCustomerId}
                      error={currentErrors.reqLPlantId}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqLPlantId: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                    />
                  </div>
                  <div className="two fields ">
                    <Dropdown
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Operation Type"
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_operation_type"
                      error={currentErrors.reqOperationType}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqOperationType: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="operation_type"
                      style={{ height: "20px" }}
                      options={operationTypeOptions}
                      value={requestInitiation?.reqOperationType}
                      onChange={(e, data) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqOperationType: data.value,
                        }))
                      }
                    />

                    <Dropdown
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Shipment Category"
                      error={currentErrors.reqExportImport}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_export_import"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqExportImport: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      placeholder="Shipment Category"
                      options={dropdown.shipmentCategoryOptions}
                      value={requestInitiation?.reqExportImport}
                      style={{
                        height: "20px",
                      }}
                      onChange={(e, data) => {
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqExportImport: data.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="two fields ">
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      required={true}
                      label="Contact Person"
                      error={currentErrors.reqContactPerson}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_contact_person"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqContactPerson: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="contact_person"
                      value={requestInitiation?.reqContactPerson}
                      onChange={(e) => {
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqContactPerson: e.target.value,
                        }));
                      }}
                    />
                    <Dropdown
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Department"
                      error={currentErrors.reqDepartment}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_department"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqDepartment: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      search={true}
                      placeholder="Departments"
                      options={dropdown.departmentTypeOptions}
                      value={requestInitiation?.reqDepartment}
                      style={{ height: "20px" }}
                      onChange={(e, data) => {
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqDepartment: data.value,
                        }));
                      }}
                    />
                  </div>

                  <div className="two fields ">
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      required={true}
                      label="Contact Number"
                      error={currentErrors.reqContactNumber}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_contact_number"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqContactNumber: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      type="tel"
                      name="contact_number"
                      pattern="(^[0-9]{10}$)"
                      value={requestInitiation?.reqContactNumber}
                      onChange={(e) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqContactNumber: e.target.value,
                        }))
                      }
                    />
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Email"
                      type="email"
                      error={currentErrors.reqContactEmail}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_contact_email"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqContactEmail: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="contact_email"
                      value={requestInitiation?.reqContactEmail}
                      onChange={(e) => {
                        const emailValue = e.target.value;
                        if (validateEmail(emailValue)) {
                          setRequestInitiation((prevState) => ({
                            ...prevState,
                            reqContactEmail: e.target.value,
                          }));

                          setCurrentErrors((prevState) => {
                            const { reqContactEmail: _, ...rest } = prevState;
                            return rest;
                          });
                        } else {
                          setCurrentErrors((prevState) => ({
                            ...prevState,
                            reqContactEmail: "Invalid email address",
                          }));
                        }
                      }}
                    />
                  </div>

                  <div className="two fields ">
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="P.O No"
                      error={currentErrors.reqPono}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_pono"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqPono: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      placeholder="P.O No"
                      value={requestInitiation?.reqPono}
                      onChange={(e) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqPono: e.target.value,
                        }))
                      }
                    />
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="B/L No"
                      error={currentErrors.reqBlno}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_blno"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqBlno: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      placeholder="B/L Number"
                      value={requestInitiation?.reqBlno}
                      onChange={(e) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqBlno: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="two fields ">
                    <DateTimePickerInput
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      reset={searchFieldReset}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_booking_required_date"
                      setReset={setSearchFieldReset}
                      value={requestInitiation?.reqBookingRequiredDate}
                      onAccept={(value) => {
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqBookingRequiredDate: value,
                          reqBookingRequiredDateFormatted:
                            formatFromJsonDate(value),
                        }));
                        dispatch(
                          requestActions.setRequest({
                            reqBookingRequiredDate: value,
                            // tempBookingRequiredDate: value,
                          })
                        );
                      }}
                      validators={
                        !allowBackDates
                          ? [
                              {
                                func: (date) => isAfter(date, new Date()),
                                message:
                                  "Date and Time should be after current date and time",
                              },
                            ]
                          : []
                      }
                      error={currentErrors.reqBookingRequiredDate}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqBookingRequiredDate: _, ...rest } =
                            prevState;
                          return rest;
                        })
                      }
                      required={false}
                      label="Booking Required Date"
                    />
                    <DateTimePickerInput
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      reset={searchFieldReset}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_delivery_cutoff"
                      setReset={setSearchFieldReset}
                      value={requestInitiation?.reqDeliveryCutoff}
                      style={{
                        borderColor: currentErrors.departureTime ? "red" : "",
                      }}
                      onAccept={(value) => {
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqDeliveryCutoff: value,
                          reqDeliveryCutoffFormatted: formatFromJsonDate(value),
                        }));
                      }}
                      validators={[
                        {
                          func: (date) => isAfter(date, new Date()),
                          message:
                            "Date and Time should be after current date and time",
                        },
                      ]}
                      error={currentErrors.reqDeliveryCutoff}
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqDeliveryCutoff: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      label="Cut off Time"
                    />
                  </div>

                  <div className="two fields ">
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Packages"
                      error={currentErrors.reqPackages}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_packages"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqPackages: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      type="number"
                      min={0}
                      placeholder="Packages"
                      value={requestInitiation?.reqPackages}
                      onChange={(e) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqPackages: e.target.value,
                        }))
                      }
                    />
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Reference Number"
                      error={currentErrors.reqReferanceValue}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_referance_value"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqReferanceValue: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="ref_value"
                      value={requestInitiation?.reqReferanceValue}
                      onChange={(e) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqReferanceValue: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="two fields ">
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Invoice Ref No"
                      error={currentErrors.reqInvoiceRef}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_invoice_ref"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqInvoiceRef: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="inv_ref"
                      value={requestInitiation?.reqInvoiceRef}
                      onChange={(e) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqInvoiceRef: e.target.value,
                        }))
                      }
                    />
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Vessel Number"
                      error={currentErrors.reqVessel}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_vessel"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqVessel: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="vessel_number"
                      value={requestInitiation?.reqVessel}
                      onChange={(e) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqVessel: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <div className="two fields ">
                    <Input
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      label="Voyage Number"
                      error={currentErrors.reqVayage}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_vayage"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqVayage: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      name="voy_number"
                      value={requestInitiation?.reqVayage}
                      onChange={(e) =>
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqVayage: e.target.value,
                        }))
                      }
                    />
                    <Dropdown
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      required={true}
                      label="Consolidate(yes/no)"
                      error={currentErrors.reqConsolidateAllowed}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_consolidate_allowed"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqConsolidateAllowed: _, ...rest } =
                            prevState;
                          return rest;
                        })
                      }
                      placeholder="Consolidate Yes/No"
                      options={dropdown.yesNoOptions}
                      value={requestInitiation?.reqConsolidateAllowed}
                      style={{
                        height: "20px",
                      }}
                      onChange={(e, data) => {
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqConsolidateAllowed: data.value,
                        }));
                      }}
                    />
                  </div>
                  <div className="two fields">
                    {/* from the name change table */}
                    <Dropdown
                      readOnly={viewOnlyMode}
                      useDynamicFields={true}
                      required={true}
                      label="Request Type"
                      error={currentErrors.reqType}
                      form={FORM}
                      module={MODULES.Transaction}
                      field="req_type"
                      resetError={() =>
                        setCurrentErrors((prevState) => {
                          const { reqType: _, ...rest } = prevState;
                          return rest;
                        })
                      }
                      placeholder="Request Commitment Type"
                      options={dropdown.requestType}
                      value={requestInitiation?.reqType}
                      style={{
                        height: "20px",
                      }}
                      onChange={(e, data) => {
                        setRequestInitiation((prevState) => ({
                          ...prevState,
                          reqType: data.value,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      </>
    );
  }
);

export default RequestCreationInitiation;
